import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { samplePages } from '../data/content';
import { CtaButton } from './CtaButton';
export function LookInside() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(samplePages.length - 1, index));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let minDistance = Infinity;
        Array.from(track.children).forEach((child, index) => {
          const el = child as HTMLElement;
          const distance = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
          if (distance < minDistance) {
            minDistance = distance;
            closest = index;
          }
        });
        setActive(closest);
      });
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  const current = samplePages[active];
  return <section aria-roledescription="carousel" aria-label="Lesson components from Restaurant English Ready" className="w-full overflow-hidden bg-stone-900 py-20 text-white sm:py-28" data-id="element-118" data-mp="3sgdtrqnq3vsv">
      <div className="mx-auto max-w-3xl px-5 text-center" data-id="element-119" data-mp="2zs9kyw9uv2ly">
        <h2 className="text-[2rem] font-black leading-[1.08] tracking-tight sm:text-5xl" data-id="element-120" data-mp="2zrq65ofi4uc5">
          Take a Look <span className="italic text-red-500" data-id="element-121" data-mp="13mr3jm6xa0b8">Inside.</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/60 sm:text-base" data-id="element-122" data-mp="18mn75kbpjpq9">
          Five real pages from Volume 02 — Menus &amp; Taking Orders.
          Explore a complete lesson: the model dialogue, worksheet, role cards,
          practical task and teacher key.
        </p>

        <div role="tablist" aria-label="Choose a sample page" className="mt-8 inline-flex rounded-full bg-white/10 p-1" data-id="element-123" data-mp="kraofdoqmuyt">
          {samplePages.map((page, index) => {
          const isActive = index === active;
          return <button key={page.level} type="button" role="tab" aria-selected={isActive} onClick={() => scrollToIndex(index)} className={`whitespace-nowrap rounded-full px-2.5 py-2 text-xs font-extrabold tracking-wide transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 sm:px-5 sm:text-sm ${isActive ? 'bg-red-500 text-white' : 'text-white/60 hover:text-white'}`} data-id="element-124" data-mp="tpcptucdvpmm" data-mpi={index}>
                {page.level}
              </button>;
        })}
        </div>
      </div>

      <div className="relative mt-12" data-id="element-125" data-mp="2fajaq3ijvsig">
        <div ref={trackRef} className="relative flex [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory gap-5 overflow-x-auto px-[8vw] pb-2 sm:gap-8 sm:px-[22vw] lg:px-[31vw]" data-id="element-126" data-mp="2aisvbj4raek6">
          {samplePages.map((page, index) => {
          const isActive = index === active;
          return <figure key={page.level} role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${samplePages.length}: ${page.title}`} className="w-[84vw] shrink-0 snap-center sm:w-[56vw] lg:w-[38vw]" data-id="element-127" data-mp="1wjjkz038eeo4" data-mpi={index}>
                <button type="button" onClick={() => scrollToIndex(index)} tabIndex={isActive ? -1 : 0} aria-label={`Show ${page.level} sample page`} className={`block w-full overflow-hidden rounded-2xl bg-white p-2 shadow-xl transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] focus:outline-none focus-visible:ring-4 focus-visible:ring-red-500/60 ${isActive ? 'scale-100 opacity-100' : 'scale-[0.92] opacity-40'}`} data-id="element-128" data-mp="1njdaln2txut6">
                  <img src={page.image} alt={page.alt} width="2400" height="3000" loading="lazy" className="aspect-[4/5] w-full rounded-xl object-contain" data-id="element-129" data-mp="3twta4iz197d0" />
                </button>
              </figure>;
        })}
        </div>

        <button type="button" onClick={() => scrollToIndex(active - 1)} disabled={active === 0} aria-label="Previous page" className="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-red-500 text-white shadow-xl transition-[opacity,transform,background-color] duration-150 hover:scale-105 hover:bg-red-600 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40 disabled:pointer-events-none disabled:opacity-30 sm:left-4 sm:h-12 sm:w-12 lg:left-[calc(31vw-4.5rem)]" data-id="element-130" data-mp="369l6e5h966d3">
          <ChevronLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" data-id="element-131" data-mp="2s90n787fe8j2" />
        </button>
        <button type="button" onClick={() => scrollToIndex(active + 1)} disabled={active === samplePages.length - 1} aria-label="Next page" className="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-red-500 text-white shadow-xl transition-[opacity,transform,background-color] duration-150 hover:scale-105 hover:bg-red-600 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40 disabled:pointer-events-none disabled:opacity-30 sm:right-4 sm:h-12 sm:w-12 lg:right-[calc(31vw-4.5rem)]" data-id="element-132" data-mp="28oa66pvec43b">
          <ChevronRightIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" data-id="element-133" data-mp="31cp9if560xyt" />
        </button>
      </div>

      <div className="mx-auto mt-8 max-w-md px-5 text-center" aria-live="polite" data-id="element-134" data-mp="2wedyw271qjfj">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-red-500" data-id="element-135" data-mp="3accax0xwa3rv">
          {current.caption}
        </p>
        <h3 className="mt-2 text-xl font-bold sm:text-2xl" data-id="element-136" data-mp="35flx6k9kbk9m">
          {current.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-white/60" data-id="element-137" data-mp="23266nz4v881m">
          {current.text}
        </p>

        <div className="mt-6 flex items-center justify-center gap-2 sm:hidden" data-id="element-138" data-mp="27zxvule4lg24">
          {samplePages.map((page, index) => <span key={page.level} aria-hidden="true" className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ${index === active ? 'w-6 bg-red-500' : 'w-1.5 bg-white/30'}`} data-id="element-139" data-mp="2bz6plq961dxo" data-mpi={index} />)}
        </div>

        <p className="mt-8 text-xs font-semibold text-white/50" data-id="element-140" data-mp="2qq786o3mmo7j">
          36 lessons across 6 volumes · six lessons per volume
        </p>
        <CtaButton href="#offer" className="mt-4" data-id="element-141" data-mp="2vqbr0ks0bv0x" />
      </div>
    </section>;
}